import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { Entry } from '@/types/api';
import { EntryTitle, entryTone } from './EntryItem';
import { formatDate } from './format';

const ITEM_CLASS: Record<Entry['status'], string> = {
  PENDING: 'timeline__item',
  APPROVED: 'timeline__item',
  REJECTED: 'timeline__item is-rejected',
};

function byline(t: TFunction, e: Entry): string {
  const name = e.createdBy.name;
  const parts = [
    e.source === 'DICTATION'
      ? t('chart.history.dictatedBy', { name })
      : t('chart.history.manualBy', { name }),
  ];
  if (e.corrected) parts.push(t('chart.history.corrected'));
  if (e.rejectReason) parts.push(t('chart.history.reason', { reason: e.rejectReason }));
  return parts.join(' · ');
}

export function ToothHistory({ entries }: { entries: Entry[] }) {
  const { t } = useTranslation();
  if (!entries.length) {
    return <p className="muted">{t('chart.history.empty')}</p>;
  }
  const ordered = [...entries].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <ol className="timeline">
      {ordered.map((e) => (
        <li key={e.clientId} className={ITEM_CLASS[e.status]}>
          <div className="row">
            <strong>{formatDate(e.createdAt)}</strong>
            {e.status === 'REJECTED' ? (
              <span className="badge badge--rejected">{t('chart.status.REJECTED')}</span>
            ) : (
              <span className={`kind-chip kind-chip--${entryTone(e)}`}>
                {t(`chart.kind.${entryTone(e)}`)}
              </span>
            )}
          </div>
          <div className={e.status === 'REJECTED' ? 'muted' : ''}>
            <EntryTitle entry={e} />
            {e.note && <span className="muted"> — {e.note}</span>}
          </div>
          <div className="small muted">{byline(t, e)}</div>
        </li>
      ))}
    </ol>
  );
}
